import type { Booking, Deposit, Waiver } from '@/domain/types';

/**
 * Shared deposit + waiver status chips for the confirmations views. Both the
 * worklist rows (ConfirmationCalls) and the work modal (ConfirmationWorkModal)
 * consume these so the same booking never shows different chip colours across
 * the two screens. Each returns a display label + a semantic pill class.
 *
 * Colours match the approved mock: paid deposit = mint, pending = gold, a sent
 * (but unsigned) waiver reads muted/cream, a signed waiver = mint.
 */
export interface ConfirmationChip {
  label: string;
  cls: string;
}

export function depositChip(
  booking: Pick<Booking, 'depositWaived'>,
  deposit: Deposit | undefined,
): ConfirmationChip {
  if (booking.depositWaived) return { label: 'Deposit waived', cls: 'pill-cream' };
  if (deposit?.status === 'paid') return { label: 'Deposit paid', cls: 'pill-mint' };
  if (deposit?.status === 'pending') return { label: 'Deposit pending', cls: 'pill-gold' };
  if (deposit) return { label: `Deposit ${deposit.status}`, cls: 'pill-cream' };
  return { label: 'No deposit on file', cls: 'pill-cream' };
}

export function waiverChip(waiver: Waiver | undefined): ConfirmationChip {
  if (!waiver) return { label: 'No waiver on file', cls: 'pill-cream' };
  if (waiver.status === 'signed') return { label: 'Waiver signed', cls: 'pill-mint' };
  if (waiver.status === 'sent') return { label: 'Waiver sent', cls: 'pill-cream' };
  if (waiver.status === 'expired') return { label: 'Waiver expired', cls: 'pill-cream' };
  return { label: 'Waiver not sent', cls: 'pill-cream' };
}
